import {
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
  ViewChild,
  forwardRef
} from '@angular/core';
import { TranslateService }                                from '@ngx-translate/core';
import { UIRouter }                                        from '@uirouter/core';

import { MessagesService }              from '../messages/messages.service';
import { AttachmentModel }              from './models/attachment';
import { AttachmentOptionsModel }       from './models/attachment.options';
import { AttachmentThumbnailStatusModel } from './models/attachment.thumbnail-status';
import { AttachmentsService }           from './attachments.service';

@Component({
  selector: 'wor-attachments-box',
  template: require('./attachments.box.component.html')
})
export class AttachmentsBoxComponent implements OnInit {
  @Input() attachments: Array<AttachmentModel> = [];
  @Input() options: AttachmentOptionsModel;

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  collapsed = false;
  file: File;
  showFileInput = true;
  thumbnails: AttachmentThumbnailStatusModel = {
    isLoading    : true,
    isSuccessful : false,
    percentLoaded: 0
  };
  uploading = false;

  constructor (
    @Inject(forwardRef(() => AttachmentsService)) private attachmentsService: AttachmentsService,
    @Inject(forwardRef(() => MessagesService)) private messagesService: MessagesService,
    private translateService: TranslateService,
    private uiRouter: UIRouter
  ) {}

  ngOnInit(): void {
    this.collapsed = !this.options?.open;
    this.attachmentsService.getThumbnails(this.thumbnails, this.attachments);
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file = input.files?.[0] || null;
  }

  isFileAttached(): boolean {
    return !!this.fileInput?.nativeElement?.files?.length;
  }

  isPdf(attachment: AttachmentModel): boolean {
    return this.attachmentsService.isPdf(attachment);
  }

  isVehicleVisuals(attachment: AttachmentModel): boolean {
    return this.attachmentsService.isVehicleVisuals(attachment);
  }

  isVideo(attachment: AttachmentModel): boolean {
    return this.attachmentsService.isVideo(attachment);
  }

  showAttachment(attachment: AttachmentModel): void {
    this.attachmentsService.showAttachment(attachment);
  }

  deleteAttachment(attachment: AttachmentModel): void {
    this.attachmentsService.deleteAttachment(attachment, this.options)
      .then(() => {
        this.uiRouter.stateService.reload();
        this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.DELETED'), 'success');
      })
      .catch(err => {
        this.messagesService.error(err);
      });
  }

  emailVideo(attachment: AttachmentModel): void {
    this.attachmentsService.sendVideo(attachment, {
      contact: this.options.contact,
      method : 'email',
      table  : this.options.table
    }).then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.EMAIL_SENT'), 'success');
    });
  }

  smsVideo(attachment: AttachmentModel): void {
    this.attachmentsService.sendVideo(attachment, {
      contact: this.options.contact,
      method : 'sms',
      table  : this.options.table
    }).then(() => {
      this.messagesService.show(this.translateService.instant('GENERAL_SPACE.MESSAGE.SMS_SENT'), 'success');
    });
  }

  upload(): void {
    this.uploading = true;
    this.showFileInput = false;

    this.attachmentsService.upload(this.file, this.options)
      .then((attachments: Array<AttachmentModel>) => {
        this.file = null;
        if (this.fileInput?.nativeElement) {
          this.fileInput.nativeElement.value = null;
        }

        this.attachments.length = 0;
        Array.prototype.push.apply(this.attachments, attachments);

        return this.attachmentsService.getThumbnails(this.thumbnails, attachments);
      })
      .catch(err => {
        this.messagesService.error(err);
      })
      .finally(() => {
        this.uploading = false;
        this.showFileInput = true;
      });
  }

  toggle(): void {
    this.collapsed = !this.collapsed;
  }
}
